import { makeAutoObservable } from "mobx"
import type { PagedResultDto } from "@services/dto/pagedResultDto"
import type {
  IIntegrationExecution,
  IIntegrationExecutionDetail,
  IIntegrationExecutionFilter,
  IRetryHistory,
  IDashboardSummary,
  IDailyExecutionMetric,
  IEntityTypeBreakdown,
  IRecentFailure,
  IHealthStatus,
  IDashboardFilter,
  IRetryPolicy,
  IAlertConfiguration,
  IHealthCheckConfig,
  IBulkRetryInput,
  IDismissDlqInput,
  IAlertRecipient,
  IEmailTemplate,
} from "@models/integrationHub/integrationHubModel"
import integrationHubService from "@services/integrationHub/integrationHubService"

class IntegrationHubStore {
  // === Execution State ===
  tableData: PagedResultDto<IIntegrationExecution> = { items: [], totalCount: 0 }
  detailData: IIntegrationExecutionDetail | null = null
  retryHistory: IRetryHistory[] = []
  flowExecutions: IIntegrationExecution[] = []

  // === Dashboard State ===
  dashboardSummary: IDashboardSummary | null = null
  timeSeries: IDailyExecutionMetric[] = []
  entityTypeBreakdown: IEntityTypeBreakdown[] = []
  recentFailures: IRecentFailure[] = []
  healthStatus: IHealthStatus | null = null

  // === Configuration State ===
  retryPolicy: IRetryPolicy | null = null
  alertConfiguration: IAlertConfiguration | null = null
  healthCheckConfig: IHealthCheckConfig | null = null

  // === Alert Recipients & Email Template State ===
  alertRecipients: IAlertRecipient[] = []
  emailTemplate: IEmailTemplate | null = null

  // === Loading States ===
  isLoading = false
  isDashboardLoading = false
  isConfigLoading = false

  constructor() {
    makeAutoObservable(this)
  }

  // === Execution Methods ===

  async getAll(params: IIntegrationExecutionFilter) {
    this.isLoading = true
    try {
      this.tableData = await integrationHubService.getAll(params)
    } finally {
      this.isLoading = false
    }
  }

  async getDetail(id: number) {
    this.isLoading = true
    try {
      this.detailData = await integrationHubService.getDetail(id)
      return this.detailData
    } finally {
      this.isLoading = false
    }
  }

  async getRetryHistory(executionId: number) {
    this.isLoading = true
    try {
      this.retryHistory = await integrationHubService.getRetryHistory(executionId)
    } finally {
      this.isLoading = false
    }
  }

  async getByFlowId(flowId: string) {
    this.isLoading = true
    try {
      this.flowExecutions = await integrationHubService.getByFlowId(flowId)
    } finally {
      this.isLoading = false
    }
  }

  // === Retry Actions ===

  async retry(executionId: number) {
    await integrationHubService.retry(executionId)
  }

  async bulkRetry(input: IBulkRetryInput) {
    await integrationHubService.bulkRetry(input)
  }

  async dismissDlq(input: IDismissDlqInput) {
    await integrationHubService.dismissDlq(input)
  }

  // === Dashboard Methods ===

  async getDashboardSummary(params?: IDashboardFilter) {
    this.isDashboardLoading = true
    try {
      this.dashboardSummary = await integrationHubService.getDashboardSummary(params)
    } finally {
      this.isDashboardLoading = false
    }
  }

  async getTimeSeries(params?: IDashboardFilter) {
    this.isDashboardLoading = true
    try {
      this.timeSeries = await integrationHubService.getTimeSeries(params)
    } finally {
      this.isDashboardLoading = false
    }
  }

  async getEntityTypeBreakdown(params?: IDashboardFilter) {
    this.isDashboardLoading = true
    try {
      this.entityTypeBreakdown = await integrationHubService.getEntityTypeBreakdown(params)
    } finally {
      this.isDashboardLoading = false
    }
  }

  async getRecentFailures(count?: number) {
    this.isDashboardLoading = true
    try {
      this.recentFailures = await integrationHubService.getRecentFailures(count)
    } finally {
      this.isDashboardLoading = false
    }
  }

  async getHealthStatus() {
    try {
      this.healthStatus = await integrationHubService.getHealthStatus()
    } catch {
      this.healthStatus = null
    }
  }

  // === Configuration Methods ===

  async getRetryPolicy() {
    this.isConfigLoading = true
    try {
      this.retryPolicy = await integrationHubService.getRetryPolicy()
    } finally {
      this.isConfigLoading = false
    }
  }

  async updateRetryPolicy(input: Partial<IRetryPolicy>) {
    this.isConfigLoading = true
    try {
      await integrationHubService.updateRetryPolicy(input)
    } finally {
      this.isConfigLoading = false
    }
  }

  async getAlertConfiguration() {
    this.isConfigLoading = true
    try {
      this.alertConfiguration = await integrationHubService.getAlertConfiguration()
    } finally {
      this.isConfigLoading = false
    }
  }

  async updateAlertConfiguration(input: Partial<IAlertConfiguration>) {
    this.isConfigLoading = true
    try {
      await integrationHubService.updateAlertConfiguration(input)
    } finally {
      this.isConfigLoading = false
    }
  }

  async getHealthCheckConfig() {
    this.isConfigLoading = true
    try {
      this.healthCheckConfig = await integrationHubService.getHealthCheckConfig()
    } finally {
      this.isConfigLoading = false
    }
  }

  async updateHealthCheckConfig(input: Partial<IHealthCheckConfig>) {
    this.isConfigLoading = true
    try {
      await integrationHubService.updateHealthCheckConfig(input)
    } finally {
      this.isConfigLoading = false
    }
  }

  // === Reset Methods ===

  initDetail() {
    this.detailData = null
    this.retryHistory = []
  }

  // === Alert Recipients Methods ===

  async getAlertRecipients() {
    this.isConfigLoading = true
    try {
      this.alertRecipients = await integrationHubService.getAlertRecipients()
    } finally {
      this.isConfigLoading = false
    }
  }

  async addAlertRecipient(userId: number) {
    this.isConfigLoading = true
    try {
      await integrationHubService.addAlertRecipient(userId)
      await this.getAlertRecipients()
    } finally {
      this.isConfigLoading = false
    }
  }

  async removeAlertRecipient(userId: number) {
    this.isConfigLoading = true
    try {
      await integrationHubService.removeAlertRecipient(userId)
      await this.getAlertRecipients()
    } finally {
      this.isConfigLoading = false
    }
  }

  // === Email Template Methods ===

  async getEmailTemplate() {
    this.isConfigLoading = true
    try {
      this.emailTemplate = await integrationHubService.getEmailTemplate()
    } finally {
      this.isConfigLoading = false
    }
  }

  async updateEmailTemplate(input: { subject: string; body: string }) {
    this.isConfigLoading = true
    try {
      await integrationHubService.updateEmailTemplate(input)
    } finally {
      this.isConfigLoading = false
    }
  }

  async generateDefaultTemplate(): Promise<string> {
    this.isConfigLoading = true
    try {
      return await integrationHubService.generateDefaultTemplate()
    } finally {
      this.isConfigLoading = false
    }
  }

  async previewEmailTemplate(): Promise<string> {
    this.isConfigLoading = true
    try {
      return await integrationHubService.previewEmailTemplate()
    } finally {
      this.isConfigLoading = false
    }
  }
}

export default IntegrationHubStore
